"use client";
import {
  AppFormControlLayoutPanel,
  AppFormControlSelect,
  AppImage,
} from "@/common";
import { CloseIcon, PlusIcon, SuccessIcon } from "@/icons";
import { AppConstant } from "@/const";
import { useFullscreenChange, useHandleUploadImage } from "@/hooks";
import {
  CategoryTypeUploadEnum,
  DefaultModalGalaxyType,
  IUploadFile,
} from "@/models";
import { CommonClientUtils } from "@/utils";
import { Box, IconButton, Stack } from "@mui/material";
import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import AppUploadFileDropzone from "./AppUploadFileDropzone";
import ModalImageList from "./ModalImageList";

// import required modules
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "@/styles/swiper.css";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

// import required modules
import SwiperCore from "swiper";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
const AppFormControlUploadGalaxy = <T extends DefaultModalGalaxyType>({
  title = "Tải danh sách ảnh",
  fieldDefaultGalaxy = "",
  name,
  control,
  onSetValueForm,
  onConvertModel,
  optionUpload = {},
  categoryType,
  ownerId = "",
}: AppFormControlUploadGalaxyProps<T>) => {
  const optionUploadDropzon = {
    accept: AppConstant.ACCEPT_IMAGE,
    maxSize: AppConstant.MAX_SIZE_UPLOAD,
    ...optionUpload,
  };
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const handleUploadImages = useHandleUploadImage();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const galaxyItems: T[] = useWatch({
    control,
    name,
  });
  const handleSetValueForm = (newValue: T[]) => {
    if (onSetValueForm instanceof Function) {
      onSetValueForm(name, newValue);
      if (fieldDefaultGalaxy) {
        const albumThumb = newValue.filter((item) => Boolean(item.isDefault));
        let thumbId = null;
        if (albumThumb.length) {
          thumbId = albumThumb[0]?.id;
        } else {
          thumbId = "";
        }
        console.log(thumbId);
        onSetValueForm(fieldDefaultGalaxy, thumbId);
      }
    }
  };

  useFullscreenChange((fullscreen) => {
    setIsFullScreen(fullscreen);
  });

  const handleUploadImageAlbum = useCallback(
    async (acceptedFiles: File[]) => {
      CommonClientUtils.showLoading();
      const fileArr = await handleUploadImages(
        acceptedFiles,
        categoryType,
        ownerId
      );
      CommonClientUtils.hideLoading();
      if (fileArr.length) {
        const newConvertFileArr = onConvertModel(fileArr);
        handleSetValueForm([...galaxyItems, ...newConvertFileArr]);
        setIsOpenModal(true);
      }
    },
    [onSetValueForm, galaxyItems]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      handleUploadImageAlbum(acceptedFiles);
    },
    ...optionUploadDropzon,
  });

  const handleFullScreen = () => {
    if (galleryRef.current) {
      if (!document.fullscreenElement) {
        galleryRef.current.requestFullscreen();
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    }
  };

  const onDeleteAlbumItem = useCallback(
    (id: number | string) => {
      if (fieldDefaultGalaxy) {
        const itemDelete = galaxyItems.find((c) => c.id === id);
        if (itemDelete && itemDelete.isDefault) {
          onSetValueForm(fieldDefaultGalaxy, "");
        }
      }
      const newAttachments = galaxyItems.filter((item) => item.id !== id);
      handleSetValueForm(newAttachments);
    },
    [galaxyItems]
  );

  return (
    <AppFormControlLayoutPanel title={title} className="p-0">
      <Box className="grid grid-cols-24 gap-5">
        <Box
          className={clsx("col-span-24", {
            hidden: !isFullScreen,
            "display-inherit bg-light": isFullScreen,
          })}
          ref={galleryRef}
        >
          <>
            <Swiper
              loop={true}
              spaceBetween={10}
              navigation={true}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[FreeMode, Navigation, Thumbs]}
              className="swiper-main"
            >
              {galaxyItems.map((item, index) => {
                return (
                  <SwiperSlide onClick={handleFullScreen} key={index}>
                    <AppImage
                      className="lazyload icon-while-mode h-[324px] w-full object-cover rounded-3 select-none"
                      src={item.url}
                      alt="banner"
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <Swiper
              onSwiper={setThumbsSwiper}
              loop={true}
              spaceBetween={10}
              slidesPerView={8}
              freeMode={true}
              watchSlidesProgress={true}
              modules={[FreeMode, Navigation, Thumbs]}
              className="swiper-thumb"
              height={120}
            >
              {galaxyItems.map((item, index) => {
                return (
                  <SwiperSlide key={index}>
                    <AppImage
                      className="lazyload icon-while-mode h-[324px] w-full object-cover rounded-3"
                      src={item.url}
                      alt="banner"
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </>
        </Box>

        {galaxyItems.map((item) => (
          <Box className="col-span-4 lg:col-span-3 xl:col-span-3" key={item.id}>
            <Stack
              direction={"column"}
              sx={{
                "& :hover": {
                  opacity: 0.9,

                  "& .MuiIconButton-root": {
                    opacity: 1,
                  },
                },
              }}
            >
              <AppImage
                src={item.url}
                width={"100%"}
                height={120}
                className="rounded-1 bg-[#F3F4F6] cursor-pointer"
                onClick={handleFullScreen}
                alt={"image"}
              >
                <IconButton
                  className="absolute -top-[10px] -right-[10px]  bg-white  border border-solid border-border text-secondary  hover:bg-white p-2"
                  onClick={() => onDeleteAlbumItem(item.id)}
                >
                  <CloseIcon className="text-[10px]" />
                </IconButton>
              </AppImage>
              <Box className={clsx("text-ellipsis w-full text-center h6")}>
                {item.name}
              </Box>
              {item.isDefault && fieldDefaultGalaxy && (
                <Box className="text-center h6 flex items-center justify-center gap-2 mt-2">
                  <SuccessIcon className="text-success" /> Mặc định
                </Box>
              )}
            </Stack>
          </Box>
        ))}
        <Box className="col-span-4 lg:col-span-3 xl:col-span-3">
          {!galaxyItems.length ? (
            <AppUploadFileDropzone
              rootProp={getRootProps()}
              inputProp={getInputProps()}
              className="w-full h-[120px] border-[1.5px] border-dashed border-[#BDC1CAFF] rounded-1 bg-white justify-center items-center hover:cursor-pointer"
            >
              <PlusIcon
                sx={{ width: "40px", height: "38px", color: "primary.main" }}
              />
            </AppUploadFileDropzone>
          ) : (
            <Box
              className="flex w-full h-[120px] border-[1.5px] border-dashed border-[#BDC1CAFF] rounded-1 bg-white justify-center items-center hover:cursor-pointer"
              onClick={() => setIsOpenModal(true)}
            >
              <PlusIcon className="w-10 h-10 text-primary" />
            </Box>
          )}
        </Box>
      </Box>
      {fieldDefaultGalaxy && (
        <Box className="grid grid-cols-12 gap-5 mt-4">
          <Box className="col-span-6">
            <AppFormControlSelect
              label="Chọn ảnh làm ảnh bìa album"
              control={control}
              name={fieldDefaultGalaxy}
              onChangeValueForm={(event) => {
                const newGalaxy = galaxyItems.map((c) => {
                  if (c.id === event?.target.value) {
                    c.isDefault = true;
                  } else {
                    c.isDefault = false;
                  }
                  return c;
                });
                onSetValueForm(name, newGalaxy);
              }}
              options={galaxyItems.map((c) => {
                return {
                  value: c.id,
                  label: c.name,
                };
              })}
            />
          </Box>
        </Box>
      )}
      <ModalImageList<T>
        onSetValueForm={onSetValueForm}
        data={galaxyItems}
        open={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        name={name}
        onConvertModel={onConvertModel}
        categoryType={categoryType}
        ownerId={ownerId}
      />
    </AppFormControlLayoutPanel>
  );
};

export default AppFormControlUploadGalaxy;
export interface AppFormControlUploadGalaxyProps<T> {
  title: string;
  fieldDefaultGalaxy?: string;
  name: string;
  control: Control<any>;
  onSetValueForm: UseFormSetValue<any>;
  onConvertModel: (dataConvert: IUploadFile[]) => T[]; // convert model upload file sang modal cần lưu
  optionUpload?: Omit<DropzoneOptions, "onDrop">;
  categoryType: CategoryTypeUploadEnum;
  ownerId?: string;
}
